import store from 'store'

// Eagerly imported: the homepage, auth and the small redirect views, so the
// pages a visitor signs up through never wait on a second request.
//
// Every other public page is its own async chunk. The main bundle is downloaded
// and run before anything paints, so a visitor landing on one calculator or
// glossary term must not pay for all the others. Heavy authenticated routes
// (dashboard, admin, onboarding, settings, activity detail) are chunks too, so
// Highcharts, vue-good-table and the chart component graph stay out of it.

import HomepageView from 'views/HomepageView'
import OAuthSuccess from 'views/OAuthSuccess'

const PrivacyView = () => import(/* webpackChunkName: "legal" */ 'views/PrivacyView')
const TermsView = () => import(/* webpackChunkName: "legal" */ 'views/TermsView')
const MobileAppView = () => import(/* webpackChunkName: "mobile-app" */ 'views/MobileAppView')
const AiCyclingCoachLandingView = () => import(/* webpackChunkName: "ai-cycling-coach" */ 'views/AiCyclingCoachLandingView')
const PricingView = () => import(/* webpackChunkName: "pricing" */ 'views/PricingView')
const HelpView = () => import(/* webpackChunkName: "help" */ 'views/HelpView')
const HelpArticleView = () => import(/* webpackChunkName: "help" */ 'views/HelpArticleView')
const TrainingGlossaryView = () => import(/* webpackChunkName: "glossary" */ 'views/TrainingGlossaryView')
const ChangelogView = () => import(/* webpackChunkName: "changelog" */ 'views/ChangelogView')
const UnsubscribeView = () => import(/* webpackChunkName: "unsubscribe" */ 'views/UnsubscribeView')
const CompetitorAlternativeView = () => import(/* webpackChunkName: "alternatives" */ 'views/CompetitorAlternativeView')
const UpgradeAccountView = () => import(/* webpackChunkName: "upgrade" */ 'views/UpgradeAccountView')

// One chunk per calculator (the shell and index share "tools").
const ToolsView = () => import(/* webpackChunkName: "tools" */ 'views/ToolsView')
const ToolsIndexView = () => import(/* webpackChunkName: "tools" */ 'views/Tools/ToolsIndexView')
const ToolsTSSCalculator = () => import(/* webpackChunkName: "tool-tss" */ 'views/Tools/TSSCalculatorView')
const ToolsVariabilityIndexCalculator = () => import(/* webpackChunkName: "tool-variability-index" */ 'views/Tools/VariabilityIndexCalculatorView')
const ToolsIntensityFactorCalculator = () => import(/* webpackChunkName: "tool-intensity-factor" */ 'views/Tools/IntensityFactorCalculatorView')
const ToolsFTPCalculator = () => import(/* webpackChunkName: "tool-ftp" */ 'views/Tools/FTPCalculatorView')
const ToolsPowerToWeightCalculator = () => import(/* webpackChunkName: "tool-power-to-weight" */ 'views/Tools/PowerToWeightCalculatorView')
const ToolsPowerZonesCalculator = () => import(/* webpackChunkName: "tool-power-zones" */ 'views/Tools/PowerZonesCalculatorView')
const ToolsHRZonesCalculator = () => import(/* webpackChunkName: "tool-hr-zones" */ 'views/Tools/HRZonesCalculatorView')
const ToolsGoodFTPCalculator = () => import(/* webpackChunkName: "tool-good-ftp" */ 'views/Tools/GoodFTPCalculatorView')
const ToolsZone2Training = () => import(/* webpackChunkName: "tool-zone-2" */ 'views/Tools/Zone2Training')
const ToolsRunningPaceCalculator = () => import(/* webpackChunkName: "tool-running-pace" */ 'views/Tools/RunningPaceCalculatorView')
const ToolsLactateThresholdCalculator = () => import(/* webpackChunkName: "tool-lactate-threshold" */ 'views/Tools/LactateThresholdCalculatorView')
const ToolsVO2MaxCalculator = () => import(/* webpackChunkName: "tool-vo2-max" */ 'views/Tools/VO2MaxCalculatorView')
const ToolsCriticalPowerCalculator = () => import(/* webpackChunkName: "tool-critical-power" */ 'views/Tools/CriticalPowerCalculatorView')
const ToolsCyclingCalorieCalculator = () => import(/* webpackChunkName: "tool-calories" */ 'views/Tools/CyclingCalorieCalculatorView')
const ToolsRaceTimePredictor = () => import(/* webpackChunkName: "tool-race-time" */ 'views/Tools/RaceTimePredictorView')

import SignInView from 'views/Auth/SignInView'
import SignUpView from 'views/Auth/SignUpView'
import ReauthorizeView from 'views/Auth/ReauthorizeView'
import VerifySignInTokenView from 'views/Auth/VerifySignInTokenView'

import NotFoundView from 'views/NotFoundView'

export default [
  {
    path: '/',
    component: HomepageView,
    name: 'home'
  },
  {
    path: '/join',
    component: SignUpView,
    name: 'join'
  },
  {
    path: '/sign-in',
    component: SignInView,
    name: 'signIn'
  },
  {
    path: '/connect-with-strava',
    component: ReauthorizeView,
    name: 'reauthorize'
  },
  {
    path: '/select-plan',
    component: UpgradeAccountView,
    name: 'upgrade'
  },
  {
    path: '/magic-link',
    component: VerifySignInTokenView,
    name: 'verifySignInToken',
    props: (route) => ({
      token: String(route.query.token),
      mobile: route.query.mobile,
      redirect: route.query.redirect
    })
  },
  {
    path: '/magic-link-mobile',
    component: VerifySignInTokenView,
    name: 'verifySignInTokenMobile',
    props: (route) => ({
      token: String(route.query.token),
      mobile: route.query.mobile,
      redirect: route.query.redirect
    })
  },
  {
    path: '/app',
    component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardView'),
    name: 'dashboard',
    beforeEnter: async (to, from, next) => {
      const user = store.getters['profile/user']

      // Redirect to homepage unauthorized
      if (!user) {
        return next({ name: 'home' })
      }

      if (!user.initialized) {
        return next({ name: 'onboarding' })
      }

      if (user.no_account_connected && to.name.indexOf('settings') != 0) {
        return next({ name: 'reauthorize' })
      }

      if (!user.premium && !user.on_trial && to.name.indexOf('settings') != 0) {
        return next({ name: 'upgrade' })
      }

      next()
    },
    redirect: {
      name: 'dashboard.trends'
    },
    children: [
      {
        path: '/app/trends',
        component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardTrendsView'),
        name: 'dashboard.trends'
      },
      {
        path: '/app/activities',
        component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardActivitiesView'),
        name: 'dashboard.activities'
      },
      {
        path: '/app/activities/new',
        component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardCreateActivityView'),
        name: 'dashboard.createActivity'
      },
      {
        path: '/app/activities/upload',
        component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardUploadActivityView'),
        name: 'dashboard.uploadActivity'
      },
      {
        path: '/app/analyze',
        component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardAnalyzeView'),
        name: 'dashboard.analyze',
        redirect: {
          name: 'dashboard.fitness'
        },
        children: [
          {
            path: '/app/analyze/fitness',
            component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardFitnessView'),
            name: 'dashboard.fitness'
          },
          {
            path: '/app/analyze/powerProfile',
            component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardPowerProfileView'),
            name: 'dashboard.powerProfile'
          },
          {
            path: '/app/analyze/power',
            component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardPowerView'),
            name: 'dashboard.power'
          },
          {
            path: '/app/analyze/speed',
            component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardSpeedView'),
            name: 'dashboard.speed'
          },
          {
            path: '/app/analyze/distance',
            component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardDistanceView'),
            name: 'dashboard.distance'
          },
          {
            path: '/app/analyze/heartrate',
            component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardHeartrateView'),
            name: 'dashboard.heartrate'
          },
          {
            path: '/app/analyze/recovery',
            component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardRecoveryView'),
            name: 'dashboard.recovery'
          },
          {
            path: '/app/analyze/climbing',
            component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardClimbingView'),
            name: 'dashboard.climbing'
          },
          {
            path: '/app/analyze/critical-power',
            component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardCriticalPowerView'),
            name: 'dashboard.criticalPower'
          },
          {
            path: '/app/analyze/year-over-year',
            component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardYearOverYearView'),
            name: 'dashboard.year-over-year'
          }
        ]
      },
      {
        path: '/app/calendar',
        component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardCalendarView'),
        name: 'dashboard.calendar'
      },
      {
        // Training Planner (beta) — one screen in its own chunk behind the /app
        // auth + premium gate; ?day=YYYY-MM-DD opens a session sheet.
        path: '/app/plan',
        component: () => import(/* webpackChunkName: "planner" */ 'views/Planner/PlannerView'),
        name: 'dashboard.plan',
        props: (route) => ({ day: route.query.day || null })
      },
      {
        // Plan setup: the plan's stable preferences on a page of their own.
        path: '/app/plan/setup',
        component: () => import(/* webpackChunkName: "planner" */ 'views/Planner/PlannerSetupView'),
        name: 'dashboard.plan.setup',
        props: (route) => ({ focus: route.query.focus || null })
      },
      {
        path: '/app/achievements',
        component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardAchievementsView'),
        name: 'dashboard.achievements'
      },
      {
        path: '/app/ai-coach',
        component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardAiCoachView'),
        name: 'dashboard.aiCoach',
        props: (route) => ({
          conversationId: route.query.conversationId || null,
          initialPrompt: route.query.prompt || null
        })
      },
      {
        path: '/app/ai-coach/conversations',
        component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardConversationsListView'),
        name: 'dashboard.aiCoachConversations'
      },
      {
        path: '/app/athlete-profile',
        component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardAthleteProfileView'),
        name: 'dashboard.athleteProfile'
      },
      {
        path: '/app/what-is-new',
        component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardChangelogView'),
        name: 'dashboard.changelog'
      },
      {
        path: '/app/activities/:id',
        component: () => import(/* webpackChunkName: "dashboard" */ 'views/Dashboard/DashboardActivityDetailView'),
        name: 'dashboard.activity',
        props: (route) => ({
          activityId: String(route.params.id)
        }),
        redirect: {
          name: 'activity.overview'
        },
        children: [
          {
            path: '/app/activities/:id',
            name: 'activity.overview',
            component: () => import(/* webpackChunkName: "activity" */ 'views/Dashboard/Activity/ActivityAnalyzeView')
          },
          {
            path: '/app/activities/:id/analyze',
            name: 'activity.analyze',
            component: () => import(/* webpackChunkName: "activity" */ 'views/Dashboard/Activity/ActivityAnalyzeView'),
            meta: {
              hideSidebar: true
            }
          },
          {
            path: '/app/activities/:id/power',
            name: 'activity.power',
            component: () => import(/* webpackChunkName: "activity" */ 'views/Dashboard/Activity/ActivityPowerView')
          },
          {
            path: '/app/activities/:id/heart-rate',
            name: 'activity.heartrate',
            component: () => import(/* webpackChunkName: "activity" */ 'views/Dashboard/Activity/ActivityHeartRateView')
          },
          {
            path: '/app/activities/:id/wbal',
            name: 'activity.wbal',
            component: () => import(/* webpackChunkName: "activity" */ 'views/Dashboard/Activity/ActivityWbalView')
          },
          {
            path: '/app/activities/:id/segments',
            name: 'activity.segments',
            component: () => import(/* webpackChunkName: "activity" */ 'views/Dashboard/Activity/ActivitySegmentsView')
          },
          {
            path: '/app/activities/:id/laps',
            name: 'activity.laps',
            component: () => import(/* webpackChunkName: "activity" */ 'views/Dashboard/Activity/ActivityLapsView')
          },
          {
            path: '/app/activities/:id/profile',
            name: 'activity.profile',
            component: () => import(/* webpackChunkName: "activity" */ 'views/Dashboard/Activity/ActivityProfileView')
          },
          {
            path: '/app/activities/:id/similar',
            name: 'activity.similar',
            component: () => import(/* webpackChunkName: "activity" */ 'views/Dashboard/Activity/DashboardActivitySimilarRidesView')
          }
        ]
      },
      {
        path: '/app/settings',
        component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsView'),
        name: 'settings',
        redirect: {
          name: 'settings.profile'
        },
        children: [
          {
            path: '/settings/profile',
            name: 'settings.profile',
            component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsProfile')
          },
          {
            path: '/settings/ai-coach',
            name: 'settings.ai-coach',
            component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsAiCoach')
          },
          {
            path: '/settings/heartrate',
            name: 'settings.heartrate',
            component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsHeartRate')
          },
          {
            path: '/settings/heartrate-zones',
            name: 'settings.heartrate-zones',
            component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsHeartRateZones')
          },
          {
            path: '/settings/power',
            name: 'settings.power',
            component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsFtp')
          },
          {
            path: '/settings/weight',
            name: 'settings.weight',
            component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsWeight')
          },
          {
            path: '/settings/account',
            name: 'settings.account',
            component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsAccount')
          },
          {
            path: '/settings/connections',
            name: 'settings.connections',
            component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsConnections')
          },
          {
            path: '/settings/membership',
            name: 'settings.membership',
            component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsMembership')
          },
          {
            path: '/settings/notifications',
            name: 'settings.notifications',
            component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsNotifications')
          },
          {
            path: '/settings/other-sports',
            name: 'settings.other-sports',
            component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsOtherSports')
          },
          {
            path: '/settings/ambassador',
            name: 'settings.ambassador',
            component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsAmbassador')
          }
        ]
      },
      {
        path: '/settings/membership/cancel',
        name: 'settings.membership.cancel',
        component: () => import(/* webpackChunkName: "settings" */ 'views/CancelSubscriptionView')
      }
    ]
  },
  {
    path: '/hello',
    component: () => import(/* webpackChunkName: "onboarding" */ 'views/Onboarding/OnboardingView'),
    name: 'onboarding',
    beforeEnter: async (to, from, next) => {
      const user = store.getters['profile/user']

      // Redirect to homepage unauthorized
      if (!user) {
        return next({ name: 'home' })
      }

      // if (user.initialized) {
      //   return next({ name: 'dashboard' })
      // }

      next()
    },
    redirect: {
      name: 'onboarding.account'
    },
    children: [
      {
        path: '/hello/account',
        name: 'onboarding.account',
        component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsAccount')
      },
      {
        // Last onboarding step. Already-subscribed users (upgraders, or someone who
        // paid then abandoned) are auto-completed by OnboardingView rather than shown
        // the checkout — so no skip guard here.
        path: '/hello/subscription',
        name: 'onboarding.subscription',
        component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsSubscription')
      },
      {
        path: '/hello/profile',
        name: 'onboarding.profile',
        component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsProfile')
      },
      {
        path: '/hello/heartrate',
        name: 'onboarding.heartrate',
        component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsHeartRate')
      },
      {
        path: '/hello/heartrate-zones',
        name: 'onboarding.heartrate-zones',
        component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsHeartRateZones')
      },
      {
        path: '/hello/power',
        name: 'onboarding.power',
        component: () => import(/* webpackChunkName: "settings" */ 'views/Settings/SettingsPower')
      }
    ]
  },
  {
    path: '/oauth/strava/success',
    name: 'oauth.success.strava',
    component: OAuthSuccess
  },
  {
    path: '/oauth/garmin/success',
    name: 'oauth.success.garmin',
    component: OAuthSuccess
  },

  // Static pages
  {
    path: '/privacy',
    name: 'privacy',
    component: PrivacyView
  },
  {
    path: '/terms',
    name: 'terms',
    component: TermsView
  },
  {
    path: '/dpa',
    redirect: { name: 'privacy' }
  },
  {
    path: '/pricing',
    name: 'pricing',
    component: PricingView
  },
  {
    path: '/ambassadors',
    name: 'ambassadors',
    component: () => import(/* webpackChunkName: "ambassadors" */ 'views/AmbassadorsView')
  },
  {
    path: '/help',
    name: 'help',
    component: HelpView
  },
  {
    path: '/help/:slug',
    name: 'helpArticle',
    component: HelpArticleView,
    props: (route) => ({
      slug: route.params.slug
    })
  },
  {
    path: '/mobile-app',
    name: 'mobile',
    component: MobileAppView
  },
  {
    path: '/todays-plan-alternative',
    name: 'todaysPlan',
    component: CompetitorAlternativeView,
    props: { slug: 'todays-plan' }
  },
  {
    path: '/trainingpeaks-alternative',
    name: 'trainingPeaksAlternative',
    component: CompetitorAlternativeView,
    props: { slug: 'trainingpeaks' }
  },
  {
    path: '/trainerroad-alternative',
    name: 'trainerRoadAlternative',
    component: CompetitorAlternativeView,
    props: { slug: 'trainerroad' }
  },
  {
    path: '/strava-alternative',
    name: 'stravaAlternative',
    component: CompetitorAlternativeView,
    props: { slug: 'strava' }
  },
  {
    path: '/intervals-icu-alternative',
    name: 'intervalsIcuAlternative',
    component: CompetitorAlternativeView,
    props: { slug: 'intervals-icu' }
  },
  {
    path: '/ai-cycling-coach',
    name: 'aiCyclingCoach',
    component: AiCyclingCoachLandingView
  },
  {
    // Public landing page for the adaptive training plan (the planner itself is /app/plan).
    path: '/cycling-training-plans',
    name: 'cyclingTrainingPlans',
    component: () => import(/* webpackChunkName: "training-plans" */ 'views/TrainingPlans/TrainingPlansLandingView')
  },
  {
    path: '/training-glossary/:term?',
    name: 'training-glossary',
    component: TrainingGlossaryView,
    props: (route) => ({
      term: route.params.term
    })
  },
  {
    path: '/tools',
    component: ToolsView,
    children: [
      {
        path: '',
        name: 'tools',
        component: ToolsIndexView
      },
      {
        path: '/tools/tss-calculator',
        name: 'tools.tss',
        component: ToolsTSSCalculator
      },
      {
        path: '/tools/variability-index-calculator',
        name: 'tools.variabilityIndex',
        component: ToolsVariabilityIndexCalculator
      },
      {
        path: '/tools/intensity-factor-calculator',
        name: 'tools.intensityFactor',
        component: ToolsIntensityFactorCalculator
      },
      {
        path: '/tools/ftp-calculator',
        name: 'tools.ftp',
        component: ToolsFTPCalculator
      },
      {
        path: '/tools/power-to-weight-calculator',
        name: 'tools.powerToWeight',
        component: ToolsPowerToWeightCalculator
      },
      {
        path: '/tools/power-training-zones-calculator',
        name: 'tools.powerZones',
        component: ToolsPowerZonesCalculator
      },
      {
        path: '/tools/heart-rate-zones-calculator',
        name: 'tools.heartRateZones',
        component: ToolsHRZonesCalculator
      },
      {
        path: '/tools/how-good-is-my-ftp',
        name: 'tools.goodFtp',
        component: ToolsGoodFTPCalculator
      },
      {
        path: '/tools/zone-2-calculator',
        name: 'tools.zone2Training',
        component: ToolsZone2Training
      },
      {
        path: '/tools/running-pace-calculator',
        name: 'tools.runningPace',
        component: ToolsRunningPaceCalculator
      },
      {
        path: '/tools/lactate-threshold-calculator',
        name: 'tools.lactateThreshold',
        component: ToolsLactateThresholdCalculator
      },
      {
        path: '/tools/vo2-max-calculator',
        name: 'tools.vo2Max',
        component: ToolsVO2MaxCalculator
      },
      {
        path: '/tools/critical-power-calculator',
        name: 'tools.criticalPower',
        component: ToolsCriticalPowerCalculator
      },
      {
        path: '/tools/cycling-calorie-calculator',
        name: 'tools.calorieCalculator',
        component: ToolsCyclingCalorieCalculator
      },
      {
        path: '/tools/race-time-predictor',
        name: 'tools.raceTimePredictor',
        component: ToolsRaceTimePredictor
      },
      {
        // Lazy, unlike its siblings: it pulls in the builder's chart + store,
        // which have no business in the initial public bundle.
        path: '/tools/zwift-workout-importer',
        name: 'tools.zwiftImporter',
        component: () => import(/* webpackChunkName: "zwift-importer" */ 'views/Tools/ZwiftImporter/ZwiftImporterView')
      }
    ]
  },
  {
    // Standalone full-screen app — intentionally NOT a child of ToolsView (no sidebar
    // chrome). Lazy-loaded so its bundle stays out of the public/initial bundle.
    path: '/tools/workout-builder',
    name: 'tools.workoutBuilder',
    component: () => import(/* webpackChunkName: "workout-builder" */ 'views/WorkoutBuilder/WorkoutBuilderView')
  },
  {
    // SEO workout catalog: hub + goal landing pages + per-workout pages.
    // The old /tools/workout-builder/:slug template pages 301 here (routes.rb).
    path: '/cycling-workouts',
    component: () => import(/* webpackChunkName: "cycling-workouts" */ 'views/CyclingWorkouts/CyclingWorkoutsShellView'),
    children: [
      {
        path: '',
        name: 'cyclingWorkouts',
        component: () => import(/* webpackChunkName: "cycling-workouts" */ 'views/CyclingWorkouts/CyclingWorkoutsIndexView')
      },
      {
        path: ':slug',
        name: 'cyclingWorkout',
        component: () => import(/* webpackChunkName: "cycling-workouts" */ 'views/CyclingWorkouts/CyclingWorkoutSlugView'),
        props: (route) => ({ slug: route.params.slug })
      }
    ]
  },
  {
    path: '/what-is-new',
    name: 'changelog',
    component: ChangelogView
  },
  {
    path: '/unsubscribe/:id/:email',
    name: 'unsubscribe',
    component: UnsubscribeView,
    props: (route) => ({
      email: route.params.email,
      id: route.params.id
    })
  },

  // Admin
  {
    path: '/admin',
    component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminView'),
    name: 'admin',
    beforeEnter: async (to, from, next) => {
      const user = store.getters['profile/user']

      // Redirect to homepage unauthorized
      if (!user) {
        return next({ name: 'signIn' })
      }

      if (!user.admin) {
        return next({ name: 'home' })
      }

      next()
    },
    redirect: {
      name: 'admin.dashboard'
    },
    children: [
      {
        path: '/admin/users',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminUsersView'),
        name: 'admin.users'
      },
      {
        path: '/admin/dashboard',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminDashboardView'),
        name: 'admin.dashboard'
      },
      {
        path: '/admin/mrr',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminMrrView'),
        name: 'admin.mrr'
      },
      {
        path: '/admin/churn',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminChurnView'),
        name: 'admin.churn'
      },
      {
        path: '/admin/unit-economics',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminUnitEconomicsView'),
        name: 'admin.unitEconomics'
      },
      {
        path: '/admin/renewals',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminRenewalsView'),
        name: 'admin.renewals'
      },
      {
        path: '/admin/cohorts',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminCohortsView'),
        name: 'admin.cohorts'
      },
      {
        path: '/admin/plans',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminPlansView'),
        name: 'admin.plans'
      },
      {
        path: '/admin/trials',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminTrialsView'),
        name: 'admin.trials'
      },
      {
        path: '/admin/acquisition',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminAcquisitionView'),
        name: 'admin.acquisition'
      },
      {
        path: '/admin/engagement',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminEngagementView'),
        name: 'admin.engagement'
      },
      {
        path: '/admin/ad-spend',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminAdSpendView'),
        name: 'admin.adSpend'
      },
      // Old pages, kept so bookmarks land somewhere useful.
      { path: '/admin/overview', redirect: { name: 'admin.dashboard' } },
      { path: '/admin/growth', redirect: { name: 'admin.trials' } },
      {
        path: '/admin/planner',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminPlannerView'),
        name: 'admin.planner'
      },
      {
        path: '/admin/ambassadors',
        component: () => import(/* webpackChunkName: "admin" */ 'views/Admin/AdminAmbassadorsView'),
        name: 'admin.ambassadors'
      }
    ]
  },

  // 404
  {
    path: '/not-found',
    name: 'notFound',
    component: NotFoundView
  },
  {
    path: '*',
    component: NotFoundView
  }
]
